import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./ActivityDropdown.module.css";

export interface ActivityOption {
  label: string;
  value: string;
}

interface ActivityDropdownProps {
  value: ActivityOption | null;
  options: ActivityOption[];
  onChange: (option: ActivityOption | null) => void;
  placeholder?: string;
}

const ActivityDropdown: React.FC<ActivityDropdownProps> = ({
  value,
  options,
  onChange,
  placeholder = "Select activity",
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const computeMenuStyle = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const menuMaxHeight = 240;
    const gap = 4;
    const minHeight = 60;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const showBelow = spaceBelow >= minHeight || spaceBelow >= spaceAbove;
    const maxHeight = showBelow
      ? Math.min(menuMaxHeight, Math.max(spaceBelow, minHeight))
      : Math.min(menuMaxHeight, spaceAbove);

    setMenuStyle({
      position: "fixed",
      top: showBelow ? rect.bottom + gap : rect.top - maxHeight - gap,
      left: rect.left,
      width: rect.width,
      maxHeight,
      zIndex: 11000,
    });
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    setOpen(true);
    computeMenuStyle();
  };

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      if (
        !triggerRef.current?.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) setOpen(false);
    };
    const onScroll = (e: Event) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside, true);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onOutside, true);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  return (
    <div className={styles.wrapper}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ""}`}
        onClick={handleToggle}
      >
        <span className={value ? styles.value : styles.placeholder}>
          {value ? value.label : placeholder}
        </span>
        {value && (
          <span
            className={styles.clear}
            role="button"
            aria-label="Clear"
            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
          >
            ×
          </span>
        )}
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
        >
          <path
            d="M2.5 4L6 7.5L9.5 4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open &&
        createPortal(
          <div ref={menuRef} style={menuStyle} className={styles.menu}>
            <div className={styles.list}>
              {options.length === 0 ? (
                <div className={styles.empty}>No options available</div>
              ) : (
                options.map((opt, index) => {
                  const isSelected = opt.value === value?.value && opt.label === value?.label;
                  return (
                    <div
                      key={`${opt.value}-${opt.label}-${index}`}
                      className={`${styles.option} ${isSelected ? styles.optionSelected : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(opt);
                        setOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          (document.fullscreenElement as HTMLElement) ?? document.body,
        )}
    </div>
  );
};

export default ActivityDropdown;
